import type { CustomerType, Department, DepartmentTask, ZenUser } from './types';

export type ZenCapability = 'queue' | 'orders' | 'create-order' | 'ledger' | 'admin' | 'dashboard';

export const SALES_AREA_ID = 'dept-sales';
export const ACCOUNTS_AREA_ID = 'dept-accounts';
export const DESIGN_AREA_ID = 'dept-design';

/** Paper form production-type checkboxes (matched case-insensitively to department names). */
export const PAPER_PRODUCTION_TYPES = [
  'SOLVENT',
  'DIGITAL',
  'UV',
  'UV FLAT BED',
  '3D SIGNAGE',
  'OTC',
] as const;

export function hasSalesAccess(user: ZenUser): boolean {
  return user.departmentIds.includes(SALES_AREA_ID);
}

export function hasAccountsAccess(user: ZenUser): boolean {
  return user.departmentIds.includes(ACCOUNTS_AREA_ID);
}

export function hasDesignAccess(user: ZenUser): boolean {
  return user.departmentIds.includes(DESIGN_AREA_ID);
}

export function defaultDesignerName(user: ZenUser): string {
  return hasSalesAccess(user) && hasDesignAccess(user) ? user.name : '';
}

export function hasBillingAccess(user: ZenUser): boolean {
  return user.isAdmin || hasAccountsAccess(user);
}

export function hasQuoteAccess(user: ZenUser): boolean {
  return hasBillingAccess(user) || hasSalesAccess(user);
}

export function customerTypeLabel(type: CustomerType): string {
  return type === 'b2b' ? 'Supplier' : 'Customer';
}

export function isTaskVisibleOnPersonalDashboard(task: DepartmentTask, userId: string): boolean {
  if (task.status === 'pending') return true;
  if (task.status === 'started') return task.pickedUpBy === userId;
  return task.finishedBy === userId || task.pickedUpBy === userId;
}

export function tasksForQueue(tasks: DepartmentTask[], completed: boolean): DepartmentTask[] {
  return tasks.filter((task) => completed ? task.status === 'completed' : task.status !== 'completed');
}

/** Design queue tasks are visible only to the designer named as contact/graphic designer on the order. */
export function isDesignTaskVisibleToUser(
  task: DepartmentTask,
  contactPerson: string | undefined,
  user: ZenUser,
): boolean {
  if (task.departmentId !== DESIGN_AREA_ID) return true;
  if (user.isAdmin || hasAccountsAccess(user)) return true;
  if (!hasDesignAccess(user)) return true;
  return contactPerson?.trim() === user.name.trim();
}

export function landingPath(user: ZenUser, departments: Department[]): string | null {
  // Every role lands on its next action: Admin and Accounts on the dashboard,
  // Sales on a new work order, workers on their queue.
  if (user.isAdmin) return '/dashboard';
  if (hasCapability(user, departments, 'create-order')) return '/work-orders/new';
  if (hasCapability(user, departments, 'queue')) return '/queue';
  if (hasCapability(user, departments, 'dashboard')) return '/dashboard';
  if (hasCapability(user, departments, 'orders')) return '/work-orders';
  return null;
}

export function queueDepartmentIds(user: ZenUser, departments: Department[]): string[] {
  if (user.isAdmin) return [];
  const queueIds = new Set(
    departments
      .filter((department) => department.active && department.queueEnabled)
      .map((department) => department.id),
  );
  return user.departmentIds.filter((id) => queueIds.has(id));
}

export function hasCapability(
  user: ZenUser,
  departments: Department[],
  capability: ZenCapability,
): boolean {
  if (capability === 'admin') return user.isAdmin;
  if (capability === 'queue') return queueDepartmentIds(user, departments).length > 0;
  if (capability === 'create-order') return user.isAdmin || hasSalesAccess(user);
  if (capability === 'ledger' || capability === 'dashboard') return hasBillingAccess(user);
  return user.isAdmin || hasSalesAccess(user) || hasAccountsAccess(user);
}
